import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Send as SendIcon,
  CallReceived as ReceivedIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';

const RealtimeChats = ({ chats }) => {
  const [expandedChats, setExpandedChats] = useState({});

  useEffect(() => {
    // Auto-expandir el primer chat si hay chats disponibles
    if (chats.length > 0 && Object.keys(expandedChats).length === 0) {
      setExpandedChats({ [chats[0].id]: true });
    }
  }, [chats, expandedChats]);

  const handleAccordionChange = (chatId) => (event, isExpanded) => {
    setExpandedChats(prev => ({
      ...prev,
      [chatId]: isExpanded
    }));
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Función para mostrar intents - OCULTAR TODOS
  const getDisplayIntent = (intent) => {
    // Ocultar TODOS los intents
    return '';
  };

  const getRecentMessages = (messages) => {
    if (!messages) return [];
    return messages
      .sort((a, b) => {
        const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
        return dateB - dateA;
      })
      .slice(0, 10); // Máximo 10 mensajes
  };

  const getLastMessageTime = (messages) => {
    if (!messages || messages.length === 0) return null;
    
    const lastMessage = messages[messages.length - 1];
    const date = lastMessage.timestamp?.toDate ? lastMessage.timestamp.toDate() : new Date(lastMessage.timestamp);
    
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Ahora';
    if (diffMinutes < 60) return `Hace ${diffMinutes}m`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `Hace ${diffHours}h`;
    
    return date.toLocaleDateString('es-ES');
  };

  if (!chats || chats.length === 0) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        sx={{ height: 300 }}
      >
        <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body2" color="textSecondary">
          No hay chats activos en este momento
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {chats.map((chat) => {
        const recentMessages = getRecentMessages(chat.messages);
        const messageCount = chat.messages ? chat.messages.length : 0;
        const lastMessageTime = getLastMessageTime(chat.messages);

        return (
          <Accordion
            key={chat.id}
            expanded={expandedChats[chat.id] || false}
            onChange={handleAccordionChange(chat.id)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: expandedChats[chat.id] ? 'action.selected' : 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Box display="flex" alignItems="center" width="100%">
                <Badge
                  badgeContent={messageCount}
                  color="primary"
                  sx={{ mr: 2 }}
                >
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <PersonIcon />
                  </Avatar>
                </Badge>
                
                <Box flex={1}>
                  <Typography variant="subtitle2" noWrap>
                    {chat.phoneNumber || chat.id}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="textSecondary">
                      {lastMessageTime}
                    </Typography>
                    <Chip
                      label={`${messageCount} mensajes`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails sx={{ p: 0 }}>
              <List dense>
                {recentMessages.map((message, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        backgroundColor: message.type === 'sent' ? 'primary.light' : 'grey.100',
                        color: message.type === 'sent' ? 'primary.contrastText' : 'text.primary',
                        mb: 0.5,
                        borderRadius: 1,
                        mx: 1
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" width="100%">
                        {message.type === 'sent' ? (
                          <SendIcon sx={{ fontSize: 16, mr: 1, mt: 0.5, opacity: 0.7 }} />
                        ) : (
                          <ReceivedIcon sx={{ fontSize: 16, mr: 1, mt: 0.5, opacity: 0.7 }} />
                        )}
                        
                        <ListItemText
                          primary={
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                wordBreak: 'break-word',
                                color: 'inherit'
                              }}
                            >
                              {message.message}
                            </Typography>
                          }
                          secondary={
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: message.type === 'sent' ? 'primary.contrastText' : 'text.secondary',
                                  opacity: 0.8
                                }}
                              >
                                {getDisplayIntent(message.intent)}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: message.type === 'sent' ? 'primary.contrastText' : 'text.secondary',
                                  opacity: 0.8
                                }}
                              >
                                {formatMessageTime(message.timestamp)}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </ListItem>
                  </React.Fragment>
                ))}
                
                {recentMessages.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="textSecondary" align="center">
                          No hay mensajes en este chat
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default RealtimeChats;
